import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaEye, FaSearch, FaTimes, FaTruck, FaCalendarAlt,
  FaPhone, FaIdCard, FaBox, FaCheckCircle, FaClock, FaShippingFast, FaBan } from 'react-icons/fa';

const StockItemsDispatch = () => {
  const { user } = useContext(AuthContext);
  const userId = user?.user?._id;
  const companyId = user?.user?.company?._id || user?.user?.company;

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // State
  const [orders, setOrders] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dispatchId, setDispatchId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);

  const [form, setForm] = useState({
    orderId: null,
    items: [],
    dispatchDate: new Date().toISOString().split('T')[0],
    transportDetails: { vehicleNumber: '', driverName: '', driverPhone: '' },
    expectedDeliveryDate: '',
    remarks: '',
    createdBy: userId
  });

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);

  // ── Data Loading ──
  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get(`/stock-items-orders?company=${companyId}&status=pending,partially-dispatched&limit=100`);
      setOrders(response.data?.data?.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  }, [companyId]);

  const fetchDispatches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/stock-items-dispatch?company=${companyId}&limit=50`);
      setDispatches(response.data?.data?.dispatches || []);
    } catch (error) {
      console.error('Failed to fetch dispatches:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchOrders(); fetchDispatches(); }, [fetchOrders, fetchDispatches]);

  // ── Order Selection ──
  const handleOrderSelect = async (order) => {
    try {
      const response = await api.get(`/stock-items-orders/${order._id}`);
      const orderData = response.data?.data;
      if (!orderData) return toast.error('Failed to load order details');

      const items = orderData.items.map(item => {
        const pending = (item.orderedQuantity || 0) - (item.dispatchedQuantity || 0);
        return {
          stockItemId: item.stockItemId?._id || item.stockItemId,
          itemName: item.stockItemId?.name || item.itemName || 'Unknown',
          unitOfMeasure: item.stockItemId?.unitOfMeasure || '',
          orderedQuantity: item.orderedQuantity || 0,
          dispatchedQuantity: item.dispatchedQuantity || 0,
          pendingQuantity: Math.max(0, pending),
          dispatchingQuantity: 0
        };
      }).filter(item => item.pendingQuantity > 0);

      if (items.length === 0) {
        return toast.warning('No pending items to dispatch in this order');
      }

      setSelectedOrder(orderData);
      setForm(prev => ({ ...prev, orderId: orderData._id, items }));
      setShowOrderModal(false);
      setOrderSearch('');
    } catch (error) {
      console.error('Order select error:', error);
      toast.error('Failed to load order details');
    }
  };

  const handleItemQuantityChange = (index, value) => {
    const qty = parseFloat(value) || 0;
    const maxQty = form.items[index].pendingQuantity;
    setForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], dispatchingQuantity: Math.min(Math.max(0, qty), maxQty) };
      return { ...prev, items };
    });
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.orderId) return toast.error('Please select an order');
    const validItems = form.items.filter(i => i.dispatchingQuantity > 0);
    if (validItems.length === 0) return toast.error('Please enter dispatch quantity for at least one item');

    try {
      setSubmitting(true);
      const payload = {
        orderId: form.orderId,
        items: validItems.map(i => ({
          stockItemId: i.stockItemId,
          dispatchedQuantity: i.dispatchingQuantity
        })),
        dispatchDate: form.dispatchDate,
        transportDetails: form.transportDetails,
        expectedDeliveryDate: form.expectedDeliveryDate || undefined,
        remarks: form.remarks || undefined,
        createdBy: userId
      };

      if (isEditing && dispatchId) {
        await api.put(`/stock-items-dispatch/${dispatchId}`, payload);
        toast.success('Dispatch updated successfully');
      } else {
        await api.post('/stock-items-dispatch', payload);
        toast.success('Dispatch created successfully');
      }
      handleReset();
      fetchOrders();
      fetchDispatches();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Failed to save dispatch');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({
      orderId: null, items: [],
      dispatchDate: new Date().toISOString().split('T')[0],
      transportDetails: { vehicleNumber: '', driverName: '', driverPhone: '' },
      expectedDeliveryDate: '', remarks: '', createdBy: userId
    });
    setSelectedOrder(null);
    setIsEditing(false);
    setDispatchId(null);
  };

  const handleViewDispatch = async (dispatch) => {
    try {
      const response = await api.get(`/stock-items-dispatch/${dispatch._id}`);
      setSelectedDispatch(response.data?.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to load dispatch details');
    }
  };

  // ── Status Badge ──
  const getStatusBadge = (status) => {
    const config = {
      'pending': { bg: 'bg-yellow-100 text-yellow-800', icon: <FaClock className="mr-1" />, label: 'Pending' },
      'in-transit': { bg: 'bg-blue-100 text-blue-800', icon: <FaTruck className="mr-1" />, label: 'In Transit' },
      'partial': { bg: 'bg-orange-100 text-orange-800', icon: <FaBox className="mr-1" />, label: 'Partial' },
      'delivered': { bg: 'bg-green-100 text-green-800', icon: <FaCheckCircle className="mr-1" />, label: 'Delivered' },
      'cancelled': { bg: 'bg-red-100 text-red-800', icon: <FaBan className="mr-1" />, label: 'Cancelled' }
    };
    const c = config[status] || { bg: 'bg-gray-100 text-gray-800', label: status };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg}`}>{c.icon}{c.label}</span>;
  };

  const filteredOrdersForModal = orders.filter(o => {
    if (!orderSearch.trim()) return true;
    const term = orderSearch.toLowerCase();
    return o.orderId?.toLowerCase().includes(term) || o.supplier?.name?.toLowerCase().includes(term);
  });

  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const inputBg = darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className={`min-h-screen p-4 md:p-6 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-3 rounded-xl"><FaShippingFast className="text-white w-8 h-8" /></div>
          <div>
            <h1 className="text-2xl font-bold text-white">Stock Items Dispatch</h1>
            <p className="text-white/80 text-sm">Create dispatch entries for stock item orders</p>
          </div>
        </div>
      </div>

      {/* Dispatch Form */}
      <div className={`${cardBg} border rounded-2xl shadow-lg mb-6 overflow-hidden`}>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaPlus className="w-4 h-4" />{isEditing ? 'Edit Dispatch' : 'New Dispatch'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Order Selection */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Select Order *</label>
            <button type="button" onClick={() => setShowOrderModal(true)}
              className={`w-full text-left px-4 py-3 rounded-xl border-2 border-dashed transition-all ${
                selectedOrder
                  ? `${darkMode ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-300 bg-indigo-50'}`
                  : `${darkMode ? 'border-gray-600 hover:border-indigo-500' : 'border-gray-300 hover:border-indigo-400'}`
              }`}>
              {selectedOrder ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-indigo-500" />
                    <span className={`font-medium ${textPrimary}`}>{selectedOrder.orderId}</span>
                    <span className={`text-sm ${textSecondary}`}>• {selectedOrder.supplier?.name}</span>
                  </div>
                  <span className={`text-sm ${textSecondary}`}>{form.items.length} item(s) pending</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <FaSearch className={textSecondary} />
                  <span className={textSecondary}>Click to select an order for dispatch</span>
                </div>
              )}
            </button>
          </div>

          {/* Items Table (shown when order selected) */}
          {form.items.length > 0 && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>Dispatch Quantities</label>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-center">Ordered</th>
                      <th className="px-3 py-2 text-center">Dispatched</th>
                      <th className="px-3 py-2 text-center">Pending</th>
                      <th className="px-3 py-2 text-center">Dispatch Now</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {form.items.map((item, index) => (
                      <tr key={index} className={`${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                        <td className="px-3 py-2">
                          <div className={`font-medium ${textPrimary}`}>{item.itemName}</div>
                          {item.unitOfMeasure && <div className={`text-xs ${textSecondary}`}>{item.unitOfMeasure}</div>}
                        </td>
                        <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.orderedQuantity}</td>
                        <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.dispatchedQuantity}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{item.pendingQuantity}</span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="number" min="0" max={item.pendingQuantity} value={item.dispatchingQuantity || ''}
                            onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                            className={`w-24 mx-auto px-2 py-1.5 rounded-lg border text-center text-sm ${inputBg} focus:ring-2 focus:ring-indigo-500`}
                            placeholder="0" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Dates Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Dispatch Date</label>
              <input type="date" value={form.dispatchDate}
                onChange={(e) => setForm(prev => ({ ...prev, dispatchDate: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`} />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Expected Delivery Date</label>
              <input type="date" value={form.expectedDeliveryDate}
                onChange={(e) => setForm(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`} />
            </div>
          </div>

          {/* Transport Details */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>Transport Details</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <FaTruck className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                <input type="text" placeholder="Vehicle Number" value={form.transportDetails.vehicleNumber}
                  onChange={(e) => setForm(prev => ({ ...prev, transportDetails: { ...prev.transportDetails, vehicleNumber: e.target.value } }))}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border ${inputBg}`} />
              </div>
              <div className="relative">
                <FaIdCard className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                <input type="text" placeholder="Driver Name" value={form.transportDetails.driverName}
                  onChange={(e) => setForm(prev => ({ ...prev, transportDetails: { ...prev.transportDetails, driverName: e.target.value } }))}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border ${inputBg}`} />
              </div>
              <div className="relative">
                <FaPhone className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                <input type="text" placeholder="Driver Phone" value={form.transportDetails.driverPhone}
                  onChange={(e) => setForm(prev => ({ ...prev, transportDetails: { ...prev.transportDetails, driverPhone: e.target.value } }))}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border ${inputBg}`} />
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Remarks</label>
            <textarea value={form.remarks} onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
              className={`w-full px-4 py-3 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`}
              rows={2} placeholder="Optional notes..." />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button type="submit" disabled={submitting}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50">
              {submitting ? 'Saving...' : isEditing ? 'Update Dispatch' : 'Create Dispatch'}
            </button>
            <button type="button" onClick={handleReset}
              className={`px-6 py-3 rounded-xl border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} transition-colors`}>
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Dispatches List */}
      <div className={`${cardBg} border rounded-2xl shadow-lg overflow-hidden`}>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaTruck className="w-4 h-4" /> Dispatch History
          </h2>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div><p className={textSecondary}>Loading...</p></div>
          ) : dispatches.length === 0 ? (
            <div className="text-center py-12">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <FaTruck className={`w-8 h-8 ${textSecondary}`} />
              </div>
              <h3 className={`text-lg font-medium mb-1 ${textPrimary}`}>No Dispatches Yet</h3>
              <p className={textSecondary}>Create your first dispatch above</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <th className="px-4 py-3 text-left text-sm font-medium">Dispatch ID</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Order</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Items</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Date</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {dispatches.map((dispatch) => (
                    <tr key={dispatch._id} className={`${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`}>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${textPrimary}`}>{dispatch.dispatchId}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${textPrimary}`}>{dispatch.orderId?.orderId || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${textPrimary}`}>{dispatch.supplier?.name || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${darkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                          {dispatch.items?.length || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm ${textSecondary}`}>{new Date(dispatch.dispatchDate || dispatch.createdAt).toLocaleDateString('en-IN')}</span>
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(dispatch.status)}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleViewDispatch(dispatch)} title="View"
                          className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                          <FaEye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Selection Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowOrderModal(false)}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${textPrimary}`}>Select Order</h3>
              <button onClick={() => setShowOrderModal(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><FaTimes /></button>
            </div>
            <div className="p-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search by order ID or supplier..." autoFocus
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${inputBg}`} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {filteredOrdersForModal.length === 0 ? (
                <p className={`text-center py-8 ${textSecondary}`}>No orders with pending dispatch</p>
              ) : filteredOrdersForModal.map(order => (
                <button key={order._id} onClick={() => handleOrderSelect(order)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${darkMode ? 'border-gray-700 hover:border-indigo-500' : 'border-gray-200 hover:border-indigo-300'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-medium ${textPrimary}`}>{order.orderId}</span>
                      <span className={`ml-2 text-sm ${textSecondary}`}>• {order.supplier?.name}</span>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                  <div className={`text-xs mt-1 ${textSecondary}`}>
                    {order.items?.length} items • {formatCurrency(order.totalOrderValue)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* View Dispatch Modal */}
      {showViewModal && selectedDispatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowViewModal(false)}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedDispatch.dispatchId}</h3>
                <p className="text-white/70 text-sm">Order: {selectedDispatch.orderId?.orderId || 'N/A'}</p>
              </div>
              <button onClick={() => setShowViewModal(false)} className="p-2 rounded-lg text-white hover:bg-white/20"><FaTimes /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs ${textSecondary}`}>Supplier</label>
                  <p className={`font-medium ${textPrimary}`}>{selectedDispatch.supplier?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Status</label>
                  <div className="mt-1">{getStatusBadge(selectedDispatch.status)}</div>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Dispatch Date</label>
                  <p className={textPrimary}>{new Date(selectedDispatch.dispatchDate || selectedDispatch.createdAt).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Expected Delivery</label>
                  <p className={textPrimary}>{selectedDispatch.expectedDeliveryDate ? new Date(selectedDispatch.expectedDeliveryDate).toLocaleDateString('en-IN') : 'Not set'}</p>
                </div>
              </div>

              {/* Transport Details */}
              {selectedDispatch.transportDetails && (selectedDispatch.transportDetails.vehicleNumber || selectedDispatch.transportDetails.driverName) && (
                <div className={`p-3 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`text-sm font-semibold mb-2 ${textPrimary}`}>Transport Details</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className={textSecondary}>Vehicle:</span> <span className={textPrimary}>{selectedDispatch.transportDetails.vehicleNumber || '-'}</span></div>
                    <div><span className={textSecondary}>Driver:</span> <span className={textPrimary}>{selectedDispatch.transportDetails.driverName || '-'}</span></div>
                    <div><span className={textSecondary}>Phone:</span> <span className={textPrimary}>{selectedDispatch.transportDetails.driverPhone || '-'}</span></div>
                  </div>
                </div>
              )}

              <div>
                <h4 className={`text-sm font-semibold mb-2 ${textPrimary}`}>Items</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <th className={`px-3 py-2 text-left ${textSecondary}`}>Item</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Dispatched</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Received</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedDispatch.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className={`px-3 py-2 ${textPrimary}`}>{item.stockItemId?.name || 'N/A'}</td>
                          <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.dispatchedQuantity}</td>
                          <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.receivedQuantity || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedDispatch.remarks && (
                <div>
                  <label className={`text-xs ${textSecondary}`}>Remarks</label>
                  <p className={`${textPrimary} text-sm mt-1`}>{selectedDispatch.remarks}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockItemsDispatch;
